import type { CatalogLayers } from '../editor/useEditorCanvas';
import { artworkCmd } from './apiClient';

/**
 * The catalogue: the templates and graphics Flobi offers every workspace, filed under categories. Kept by
 * artwork-back (`CatalogItem`), and written by Flobi's staff from the admin app alone —
 * beside them, what a person saved for themselves, their workspace or a project.
 *
 * A drawing is kept one of two ways. As `svg`, which rides with the listing and is read
 * into layers when it is placed; or as `layers`, exactly what was on a page in the
 * editor, asked for when the item is picked.
 */

export type CatalogKind = 'template' | 'graphic';
/** Where an item lives: Flobi's own, mine, my workspace's, or a project's. */
export type CatalogPlace = 'flobi' | 'mine' | 'workspace' | 'project';
/** Where a person may save to. Never Flobi's: that is the admin app's. */
export type SavePlace = Exclude<CatalogPlace, 'flobi'>;

export interface CatalogItem {
  readonly id: string;
  readonly kind: CatalogKind;
  readonly name: string;
  readonly tags: readonly string[];
  /** What Flobi's staff filed it under. Null for one not filed, and for what people save themselves. */
  readonly category: string | null;
  readonly format: 'svg' | 'layers';
  readonly width: number;
  readonly height: number;
  /** A template kept as SVG: its page colour. */
  readonly background: string | null;
  readonly svg: string | null;
  /** The drawn picture of a template, or of anything kept as layers. */
  readonly thumbnailUrl: string | null;
  /** The picture is being drawn: asking again in a few seconds gets it. */
  readonly thumbnailPending: boolean;
  readonly place: CatalogPlace;
  /** Whether this person may take it out of the place it was saved to. */
  readonly canRemove: boolean;
  /** Whether it matches the artboard ratio requested for this template list. */
  readonly aspectMatch?: boolean;
}

export interface CatalogPage {
  readonly items: CatalogItem[];
  /** How many to skip for the next page, or null at the end. */
  readonly next: number | null;
  /** The categories Flobi's things of this kind are filed under, in staff's order. Empty for any other place. */
  readonly categories: readonly string[];
  readonly moreAspectMatches?: boolean;
}

const KINDS = new Set(['template', 'graphic']);
const PLACES = new Set(['flobi', 'mine', 'workspace', 'project']);

/** A row read loosely: anything that does not fit is left out rather than shown broken. */
function item(row: unknown): CatalogItem | null {
  const r = row as Partial<CatalogItem> | null;
  if (!r || typeof r.id !== 'string' || typeof r.name !== 'string' || !KINDS.has(r.kind as string)) return null;
  if (r.format !== 'svg' && r.format !== 'layers') return null;
  if (r.format === 'svg' && typeof r.svg !== 'string') return null;
  if (!(Number(r.width) > 0) || !(Number(r.height) > 0)) return null;
  return {
    id: r.id,
    kind: r.kind as CatalogKind,
    name: r.name,
    tags: Array.isArray(r.tags) ? r.tags.filter((tag): tag is string => typeof tag === 'string') : [],
    category: typeof r.category === 'string' && r.category ? r.category : null,
    format: r.format,
    width: Number(r.width),
    height: Number(r.height),
    background: typeof r.background === 'string' ? r.background : null,
    svg: typeof r.svg === 'string' ? r.svg : null,
    thumbnailUrl: typeof r.thumbnailUrl === 'string' ? r.thumbnailUrl : null,
    thumbnailPending: r.thumbnailPending === true,
    place: PLACES.has(r.place as string) ? (r.place as CatalogPlace) : 'flobi',
    canRemove: r.canRemove === true,
    ...(typeof r.aspectMatch === 'boolean' ? { aspectMatch: r.aspectMatch } : {}),
  };
}

export async function listCatalog(
  kind: CatalogKind,
  options: { place?: CatalogPlace; projectId?: string; search?: string; skip?: number; /** One of Flobi's categories. */ category?: string; /** Flobi's newest first, rather than in staff's order. */ recent?: boolean; limit?: number; aspectRatio?: number } = {},
): Promise<CatalogPage> {
  const flobi = !options.place || options.place === 'flobi';
  const answer = await artworkCmd<{ items?: unknown[]; next?: unknown; categories?: unknown; moreAspectMatches?: unknown }>('artwork.catalog.list', {
    kind,
    // Flobi's is what is asked for when no place is named.
    ...(!flobi ? { place: options.place } : {}),
    ...(options.place === 'project' && options.projectId ? { projectId: options.projectId } : {}),
    ...(options.search ? { search: options.search } : {}),
    // Only Flobi's are filed under categories, and only they have a "recently added".
    ...(flobi && options.category ? { category: options.category } : {}),
    ...(flobi && options.recent ? { recent: true } : {}),
    ...(options.limit ? { limit: options.limit } : {}),
    ...(options.skip ? { skip: options.skip } : {}),
    ...(kind === 'template' && Number.isFinite(options.aspectRatio) && options.aspectRatio! > 0 ? { aspectRatio: options.aspectRatio } : {}),
  });
  return {
    items: (Array.isArray(answer?.items) ? answer.items : []).map(item).filter((one): one is CatalogItem => one !== null),
    next: typeof answer?.next === 'number' ? answer.next : null,
    categories: (Array.isArray(answer?.categories) ? answer.categories : []).flatMap((one: unknown) => (typeof (one as { name?: unknown })?.name === 'string' && (one as { name: string }).name ? [(one as { name: string }).name] : [])),
    ...(typeof answer?.moreAspectMatches === 'boolean' ? { moreAspectMatches: answer.moreAspectMatches } : {}),
  };
}

/** The layers of an item kept as layers, to place it. */
export async function catalogLayers(id: string): Promise<CatalogLayers | null> {
  const answer = await artworkCmd<{ layers?: unknown }>('artwork.catalog.get', { id });
  const layers = answer?.layers as CatalogLayers | null | undefined;
  return layers && Array.isArray(layers.artwork) && layers.artwork.length > 0 ? layers : null;
}

/** Keeps an artboard as a template, or chosen objects as a graphic, in a place: mine, this project's, or the workspace's. */
export async function keepInCatalog(input: { kind: 'template' | 'graphic'; name: string; place: SavePlace; projectId?: string; layers: CatalogLayers; width?: number; height?: number }): Promise<void> {
  const size = { ...(input.width ? { width: input.width } : {}), ...(input.height ? { height: input.height } : {}) };
  await artworkCmd('artwork.catalog.keep', { kind: input.kind, name: input.name, place: input.place, ...(input.place === 'project' && input.projectId ? { projectId: input.projectId } : {}), layers: input.layers, ...size });
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('artwork:catalog-changed'));
}

/** Takes something the person saved (or, for a workspace admin, something shared) out of its place. */
export async function discardFromCatalog(id: string): Promise<void> {
  await artworkCmd('artwork.catalog.discard', { id });
}

/** An SVG as something an <img> or a mask can load. */
export function svgUrl(svg: string): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
